import { getRequest, postRequest, patchRequest, deleteRequest } from '../utils/axios';
import { buildUrlParams } from '../utils/urls';

export const audiencesService = {
  listAudiences: (onSuccess, onError, params = {}) => {
    const url = '/api/v1/audiences/' + buildUrlParams(params);
    
    getRequest(
      url,
      (response) => {
        if (onSuccess) {
          return onSuccess(response);
        }
      },
      (error, response) => {
        if (onError) {
          return onError(error, response);
        }
      }
    );
  },

  getAudience: (hash, onSuccess, onError) => {
    getRequest(
      `/api/v1/audiences/${hash}/`,
      (response) => {
        if (onSuccess) {
          return onSuccess(response);
        }
      },
      (error, response) => {
        if (onError) {
          return onError(error, response);
        }
      }
    );
  },

  createAudience: (data, onSuccess, onError) => {
    postRequest(
      '/api/v1/audiences/',
      data,
      (response) => {
        if (onSuccess) {
          return onSuccess(response);
        }
      },
      (error, response) => {
        if (onError) {
          return onError(error, response);
        }
      }
    );
  },

  getHighlights: (hash, params, onSuccess, onError) => {
    const urlParams = buildUrlParams(params);
    const url = `/api/v1/audiences/${hash}/highlights/${urlParams}`;
    getRequest(
      url,
      (response) => {
        if (onSuccess) return onSuccess(response);
      },
      (error, response) => {
        if (onError) return onError(error, response);
      }
    );
  },

  generateHighlights: (hash, params, onSuccess, onError) => {
    const urlParams = buildUrlParams(params);
    const url = `/api/v1/audiences/${hash}/highlights/generate/${urlParams}`;
    postRequest(
      url,
      {},
      (response) => {
        if (onSuccess) return onSuccess(response);
      },
      (error, response) => {
        if (onError) return onError(error, response);
      }
    );
  },

  getPopulationStatus: (hash, onSuccess, onError, params = {}) => {
    const urlParams = buildUrlParams(params);
    const url = `/api/v1/audiences/${hash}/population-status/${urlParams}`;
    
    getRequest(
      url,
      (response) => {
        if (onSuccess) {
          return onSuccess(response);
        }
      },
      (error, response) => {
        if (onError) {
          return onError(error, response);
        }
      }
    );
  },

  getAudienceCreators: (params, onSuccess, onError) => {
    const urlParams = buildUrlParams(params);
    const url = `/api/v1/audience-creators/${urlParams}`;
    
    getRequest(
      url,
      (response) => {
        if (onSuccess) {
          return onSuccess(response);
        }
      },
      (error, response) => {
        if (onError) {
          return onError(error, response);
        }
      }
    );
  },

  /**
   * Fetches one audience-scoped creator by Creator hash (detail endpoint).
   */
  getAudienceCreatorByCreatorHash: (audienceHash, creatorHash, onSuccess, onError) => {
    const qs = buildUrlParams({ audience_hash: audienceHash });
    getRequest(
      `/api/v1/audience-creators/${encodeURIComponent(creatorHash)}/` + qs,
      (response) => {
        if (onSuccess && response.data) return onSuccess(response);
        if (onError) return onError(null, response);
      },
      (error, response) => {
        if (onError) return onError(error, response);
      }
    );
  },

  getAudienceVideos: (params, onSuccess, onError) => {
    const urlParams = buildUrlParams(params);
    const url = `/api/v1/audience-videos/${urlParams}`;
    
    getRequest(
      url,
      (response) => {
        if (onSuccess) {
          return onSuccess(response);
        }
      },
      (error, response) => {
        if (onError) {
          return onError(error, response);
        }
      }
    );
  },

  getAudienceVideo: (audienceHash, videoHash, onSuccess, onError) => {
    const urlParams = buildUrlParams({ audience_hash: audienceHash });
    getRequest(
      `/api/v1/audience-videos/${videoHash}/${urlParams}`,
      (response) => {
        if (onSuccess) return onSuccess(response);
      },
      (error, response) => {
        if (onError) return onError(error, response);
      }
    );
  },

  syncAudience: (hash, onSuccess, onError) => {
    postRequest(
      `/api/v1/audiences/${hash}/sync/`,
      {},
      (response) => {
        if (onSuccess) {
          return onSuccess(response);
        }
      },
      (error, response) => {
        if (onError) {
          return onError(error, response);
        }
      }
    );
  },

  patchAudience: (hash, body, onSuccess, onError) => {
    patchRequest(
      `/api/v1/audiences/${hash}/`,
      body,
      (response) => {
        if (onSuccess) {
          return onSuccess(response);
        }
      },
      (error, response) => {
        if (onError) {
          return onError(error, response);
        }
      }
    );
  },

  deleteAudience: (hash, onSuccess, onError) => {
    deleteRequest(
      `/api/v1/audiences/${hash}/`,
      (response) => {
        if (onSuccess) {
          return onSuccess(response);
        }
      },
      (error, response) => {
        if (onError) {
          return onError(error, response);
        }
      }
    );
  },

  getAudienceRoster: (audienceHash, onSuccess, onError) => {
    const qs = buildUrlParams({ audience_hash: audienceHash });
    getRequest(
      `/api/v1/audience-roster/${qs}`,
      (response) => {
        if (onSuccess) return onSuccess(response);
      },
      (error, response) => {
        if (onError) return onError(error, response);
      }
    );
  },

  postAudienceRosterBulkAdd: (audienceHash, body, onSuccess, onError) => {
    const qs = buildUrlParams({ audience_hash: audienceHash });
    postRequest(
      `/api/v1/audience-roster/bulk-add/${qs}`,
      body,
      (response) => {
        if (onSuccess) return onSuccess(response);
      },
      (error, response) => {
        if (onError) return onError(error, response);
      }
    );
  },

  deleteAudienceRosterMember: (audienceHash, rosterHash, onSuccess, onError) => {
    const h = (rosterHash || '').trim();
    if (!h) {
      if (onError) onError(new Error('Missing roster id'), null);
      return;
    }
    deleteRequest(
      `/api/v1/audience-roster/${encodeURIComponent(h)}/`,
      (response) => {
        if (onSuccess) return onSuccess(response);
      },
      (error, response) => {
        if (onError) return onError(error, response);
      }
    );
  },
};

